import { Box, Button, Grid, GridItem, HStack, ModalBody, ModalFooter, ModalHeader, Text, useToast } from "@chakra-ui/react";
import React, { useState } from "react";
import { FormInput } from "../UI";
import * as yup from "yup";
import { useFormik } from "formik";
import { COLORS } from "../../../Helper/Colors";
import DotSpinner from "../DotSpinner";
import { Eye, EyeSlash, XCircle } from "phosphor-react";
import "react-datepicker/dist/react-datepicker.css";
import Api from "../../../Helper/ApiHandler";

const ChangePassword = ({ onClose }) => {
	const api = new Api();
	const toast = useToast();
	const [btnLoading, setBtnLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const initialValues = {
		password: "",
		newPassword: "",
		confirmPassword: "",
	};

	const schema = yup.object().shape({
		password: yup.string().required("Current password is required."),
		newPassword: yup
			.string()
			.required("New password is required.")
			.matches(
				/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
				"The password must be atleast 8 characters and contain atleast 1 uppercase character, 1 special character and 1 digit."
			),
		confirmPassword: yup
			.string()
			.oneOf([yup.ref("newPassword"), null], "New Password and Confirm password must match")
			.required("Confirm password is required."),
	});

	const handleSubmit = async (values) => {
		try {
			setBtnLoading(true);
			await api.put("auth/change-password", { oldPassword: values.password, newPassword: values.newPassword });
			onClose();
			toast({
				title: "Success.",
				description: "Password has been updated.",
				status: "success",
				duration: 9000,
				isClosable: true,
				position: "top",
			});
			setBtnLoading(false);
		} catch (error) {
			setBtnLoading(false);
			toast({
				description: error.message,
				status: "error",
				duration: 2000,
				isClosable: false,
				position: "top",
			});
		}
	};
	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: schema,
		onSubmit: handleSubmit,
	});

	const handlePasswordShow = () => {
		setShowPassword((prev) => !prev);
	};

	const handleConfirmPasswordShow = () => {
		setShowConfirmPassword((prev) => !prev);
	};

	const handleNewPasswordShow = () => {
		setShowNewPassword((prev) => !prev);
	};

	return (
		<>
			<Box
				display={"flex"}
				flexDirection={"column"}
				width={"100%"}
				height={"100%"}
				overflow={"auto"}
				padding={4}
				paddingBottom={2}
			>
				<ModalHeader display={"flex"} flex='1' width={"100%"} justifyContent={"space-between"} alignItems='center'>
					<HStack display={"flex"} flex='1' alignItems='center'>
						<Text fontWeight={"bold"} fontSize='x-large'>
							Change Password
						</Text>
					</HStack>
					<XCircle height='24' width='24' onClick={onClose} cursor={"pointer"} color={COLORS.lightGray} />
				</ModalHeader>
				<form onSubmit={formik.handleSubmit} style={{ overflow: "auto" }} className={"design-scrollbar"}>
					<ModalBody pb={6} className={"design-scrollbar"}>
						<Grid templateColumns='repeat(1, 1fr)' gap={4} width='100%'>
							<GridItem w='100%'>
								<FormInput
									onBlur={formik.handleBlur}
									type={!showPassword ? "password" : "text"}
									name='password'
									label='Enter your current password'
									placeholder='Enter your current password'
									value={formik.values.password}
									error={!!formik.touched.password && formik.errors.password}
									errorMsg={formik.errors.password}
									onChange={formik.handleChange}
									rightIcon={
										!showPassword ? (
											<Eye color={COLORS.brand} size={24} onClick={handlePasswordShow} cursor='pointer' />
										) : (
											<EyeSlash color={COLORS.brand} size={24} onClick={handlePasswordShow} cursor='pointer' />
										)
									}
								/>
							</GridItem>
							<GridItem w='100%'>
								<FormInput
									onBlur={formik.handleBlur}
									type={!showNewPassword ? "password" : "text"}
									name='newPassword'
									label='Create a New Password'
									placeholder='Enter your new password'
									value={formik.values.newPassword}
									error={!!formik.touched.newPassword && formik.errors.newPassword}
									errorMsg={formik.errors.newPassword}
									onChange={formik.handleChange}
									rightIcon={
										!showNewPassword ? (
											<Eye color={COLORS.brand} size={24} onClick={handleNewPasswordShow} cursor='pointer' />
										) : (
											<EyeSlash color={COLORS.brand} size={24} onClick={handleNewPasswordShow} cursor='pointer' />
										)
									}
								/>
							</GridItem>
							<GridItem w='100%'>
								<FormInput
									onBlur={formik.handleBlur}
									type={!showConfirmPassword ? "password" : "text"}
									name='confirmPassword'
									label='Confirm New password'
									placeholder='Confirm your new password'
									value={formik.values.confirmPassword}
									error={!!formik.touched.confirmPassword && formik.errors.confirmPassword}
									errorMsg={formik.errors.confirmPassword}
									onChange={formik.handleChange}
									rightIcon={
										!showConfirmPassword ? (
											<Eye color={COLORS.brand} size={24} onClick={handleConfirmPasswordShow} cursor='pointer' />
										) : (
											<EyeSlash color={COLORS.brand} size={24} onClick={handleConfirmPasswordShow} cursor='pointer' />
										)
									}
								/>
							</GridItem>
						</Grid>
					</ModalBody>
					<ModalFooter justifyContent={"flex-start"}>
						<Button
							isLoading={btnLoading}
							spinner={<DotSpinner key={"campaign-manager-edit-basic-form"} color={"#fff"} size={8} />}
							type='submit'
							colorScheme='brand'
							fontWeight={"normal"}
							isFullWidth
							height={14}
							className='custom-btn'
						>
							Change Password
						</Button>
					</ModalFooter>
				</form>
			</Box>
		</>
	);
};

export default ChangePassword;
